import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { routeDeliveryService } from '../../services/routeDeliveryService';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import RouteDeliveryModal from '../../components/Setup/RouteDeliveryModal';
import { useToast } from '../../components/UI/ToastContainer';
import DataGrid from '../../components/DataGrid';
import { ColumnDef } from '@tanstack/react-table';

const RouteDeliveriesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({ column: 'name', order: 'asc' });
  const pageSize = 50;
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ name?: string; status?: 'ACTIVE' | 'INACTIVE' }>({});
  const { column: sortBy, order: sortOrder } = sort;

  const { data, isLoading, error } = useQuery({
    queryKey: ['route-deliveries', currentPage, searchTerm, sortBy, sortOrder, filters],
    queryFn: () => routeDeliveryService.getRouteDeliveries({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      sortBy,
      sortOrder,
      name: filters.name,
      status: filters.status,
    })
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => routeDeliveryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['route-deliveries'] });
      setConfirmDelete(null);
      showSuccess('Route delivery deleted', 'The route delivery has been successfully deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to delete route delivery. Please try again.';
      showError('Delete failed', message);
    },
  });

  const handleExport = useCallback(async () => {
    try {
      const blob = await routeDeliveryService.export({
        search: searchTerm,
        name: filters.name,
        status: filters.status,
        ids: selectedIds.length ? selectedIds : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.style.display = 'none'; a.href = url; a.download = `route-deliveries-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed', e); }
  }, [filters.name, filters.status, searchTerm, selectedIds]);

  const rows = data?.routeDeliveries ?? [];
  const pagination = data?.pagination;
  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleBulkAction = useCallback(async (action: 'activate' | 'deactivate') => {
    try {
      if (!selectedIds.length) return;
      const status = action === 'activate' ? 'ACTIVE' : 'INACTIVE';
      await routeDeliveryService.bulkUpdateStatus(selectedIds, status);
      showSuccess('Success', `${selectedIds.length} route delivery(s) ${status.toLowerCase()}`);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['route-deliveries'] });
    } catch (e: any) {
      showError('Bulk action failed', e?.message || '');
    }
  }, [queryClient, selectedIds, showError, showSuccess]);


  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      meta: { sortable: true },
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.original.status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button onClick={() => { setSelected(row.original); setIsModalOpen(true); }} className="text-primary-600 hover:text-primary-900">
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button onClick={() => setConfirmDelete(row.original)} className="text-red-600 hover:text-red-900">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], []);

  const handleSortChange = (columnId: string) => {
    setSort((prev) => {
      if (prev.column === columnId) {
        return { column: columnId, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnId, order: 'asc' };
    });
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Error loading route deliveries.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Route Deliveries</h1>
        <div className="flex space-x-3">
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('activate')}>Activate</Button>
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('deactivate')}>Deactivate</Button>
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button onClick={() => { setSelected(null); setIsModalOpen(true); }}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search routes..."
              className="pl-10 form-input"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft">
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                type="text"
                className="form-input w-full"
                placeholder="Filter name..."
                value={filters.name ?? ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, name: e.target.value || undefined }));
                  setCurrentPage(1);
                }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-input w-full"
                value={filters.status ?? ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, status: (e.target.value as 'ACTIVE' | 'INACTIVE' | '') || undefined }));
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : (
            <DataGrid
              data={rows}
              columns={columns}
              getRowId={(row) => row.id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) =>
                setSelectedIds((prev) =>
                  checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))
                )
              }
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          )}
        </div>

        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
            </p>
            <div className="space-x-2">
              <Button variant="secondary" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <Button variant="secondary" disabled={pagination && currentPage === pagination.pages} onClick={() => setCurrentPage((p) => (pagination ? Math.min(pagination.pages, p + 1) : p + 1))}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <RouteDeliveryModal
          routeDelivery={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['route-deliveries'] }); }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Route Delivery"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default RouteDeliveriesPage;
